<script lang="ts">
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';

	import {
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
		get_gen_2_component_channel_structure,
	} from '../../../../helpers/interface_helpers';
	import { verifySettingUnsupportedFWVersion } from '../../../../helpers/firmware_helpers';

	import { devices_settings, devices_status } from '../../../../stores';

	import type { Device } from '../../../../types/Device';
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Writable } from 'svelte/store';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	import { Button, Input, toast, Text, Alert, Checkbox } from '@Shelly/shelly-design-system';
	import { makeRangePrimaryAndBold } from 'src/helpers/utilities';
	import { restrictDecimalNumberInput } from 'src/helpers/number_input_helpers';
	import { getContext, onDestroy } from 'svelte';
	import { DEVICE_MAX_CURRENT_DEFAULTS, DEVICE_MAX_POWER_DEFAULTS } from 'src/constants';
	import {
		UNDERVOLTAGE_MIN_FW,
		DEFAULT_VOLTAGE_THR,
		MIN_VOLTAGE_THR,
		MAX_VOLTAGE_THR,
		DEFAULT_AUTORECOVERY_DELAY,
		MIN_AUTORECOVERY_DELAY,
		MAX_AUTORECOVERY_DELAY,
		type VoltageProtectionLimits,
	} from './voltage_protection_constants';
	import { showNewLayout } from 'src/stores/toDelete';

	export let device: Device;
	export let limits: VoltageProtectionLimits;
	export let idPrefix: string;
	export let defaultsAlertMessage: string;
	export let defaultUndervoltageLimit: number = limits.undervoltage.min;
	export let defaultOvervoltageLimit: number = limits.overvoltage.min;
	export let showAutoRecovery = true;
	export let includeSiblingProtectionLimits = false;

	const unit = 'V';
	const secondsUnit = 's';
	const deviceType = get_gen_2_component_from_category(device);
	const index = get_device_component_index(device);
	const componentChannelStructure = get_gen_2_component_channel_structure(device);

	const MIN_UNDERVOLTAGE_LIMIT = limits.undervoltage.min;
	const MAX_UNDERVOLTAGE_LIMIT = limits.undervoltage.max;
	const MIN_OVERVOLTAGE_LIMIT = limits.overvoltage.min;
	const MAX_OVERVOLTAGE_LIMIT = limits.overvoltage.max;
	const DEFAULT_UNDERVOLTAGE_LIMIT = defaultUndervoltageLimit;
	const DEFAULT_OVERVOLTAGE_LIMIT = defaultOvervoltageLimit;

	let id = real_id(device.id);
	let settings = $devices_settings[id];

	$: supportsUndervoltage = !verifySettingUnsupportedFWVersion(
		device,
		settings,
		UNDERVOLTAGE_MIN_FW,
		$devices_status[id],
	);
	$: supportsAutoRecovery = showAutoRecovery && supportsUndervoltage;

	let settingsUndervoltageLimit = settings?.[componentChannelStructure]?.undervoltage_limit;
	let settingsOvervoltageLimit = settings?.[componentChannelStructure]?.voltage_limit;
	let settingsAutoRecovery = settings?.[componentChannelStructure]?.autorecovery_enable ?? false;
	let settingsVoltageThr = settings?.[componentChannelStructure]?.voltage_thr;
	let settingsAutorecoveryDelay = settings?.[componentChannelStructure]?.autorecovery_delay;

	let loading = false;

	let undervoltageValue = settingsUndervoltageLimit ?? DEFAULT_UNDERVOLTAGE_LIMIT;
	let undervoltageCache = undervoltageValue;
	let undervoltageError = '';

	let overvoltageValue = settingsOvervoltageLimit ?? DEFAULT_OVERVOLTAGE_LIMIT;
	let overvoltageCache = overvoltageValue;
	let overvoltageError = '';

	let autoRecoveryEnabled = settingsAutoRecovery;
	let autoRecoveryCache = settingsAutoRecovery;

	let voltageThrValue = settingsVoltageThr ?? DEFAULT_VOLTAGE_THR;
	let voltageThrCache = voltageThrValue;
	let voltageThrError = '';

	let autorecoveryDelayValue = settingsAutorecoveryDelay ?? DEFAULT_AUTORECOVERY_DELAY;
	let autorecoveryDelayCache = autorecoveryDelayValue;
	let autorecoveryDelayError = '';

	const isDeletedValue = (value: number | undefined): boolean =>
		value === undefined || Number.isNaN(value);

	const handleErrors = () => {
		undervoltageError = '';
		overvoltageError = '';
		voltageThrError = '';
		autorecoveryDelayError = '';

		if (supportsUndervoltage) {
			if (
				!isDeletedValue(undervoltageValue) &&
				(undervoltageValue < MIN_UNDERVOLTAGE_LIMIT ||
					undervoltageValue > MAX_UNDERVOLTAGE_LIMIT)
			) {
				undervoltageError = $format('error_out_of_range', {
					default: `Value ${undervoltageValue} ${unit} is out of range. Please provide a value within the limits. (${MIN_UNDERVOLTAGE_LIMIT} ${unit} — ${MAX_UNDERVOLTAGE_LIMIT} ${unit})`,
					values: {
						value: undervoltageValue,
						min: MIN_UNDERVOLTAGE_LIMIT,
						max: MAX_UNDERVOLTAGE_LIMIT,
						unit,
					},
				});
			}
		}

		if (supportsAutoRecovery) {
			const normalizedVoltageThr = isNaN(voltageThrValue) ? DEFAULT_VOLTAGE_THR : +voltageThrValue;
			if (
				!isDeletedValue(voltageThrValue) &&
				(normalizedVoltageThr < MIN_VOLTAGE_THR || normalizedVoltageThr > MAX_VOLTAGE_THR)
			) {
				voltageThrError = $format('error_out_of_range', {
					default: `Value ${normalizedVoltageThr} ${unit} is out of range. Please provide a value within the limits. (${MIN_VOLTAGE_THR} ${unit} — ${MAX_VOLTAGE_THR} ${unit})`,
					values: {
						value: normalizedVoltageThr,
						min: MIN_VOLTAGE_THR,
						max: MAX_VOLTAGE_THR,
						unit,
					},
				});
			}

			const normalizedAutorecoveryDelay = isNaN(autorecoveryDelayValue)
				? DEFAULT_AUTORECOVERY_DELAY
				: +autorecoveryDelayValue;
			if (
				!isDeletedValue(autorecoveryDelayValue) &&
				(normalizedAutorecoveryDelay < MIN_AUTORECOVERY_DELAY ||
					normalizedAutorecoveryDelay > MAX_AUTORECOVERY_DELAY)
			) {
				autorecoveryDelayError = $format('error_out_of_range', {
					default: `Value ${normalizedAutorecoveryDelay} ${secondsUnit} is out of range. Please provide a value within the limits. (${MIN_AUTORECOVERY_DELAY} ${secondsUnit} — ${MAX_AUTORECOVERY_DELAY} ${secondsUnit})`,
					values: {
						value: normalizedAutorecoveryDelay,
						min: MIN_AUTORECOVERY_DELAY,
						max: MAX_AUTORECOVERY_DELAY,
						unit: secondsUnit,
					},
				});
			}
		}

		if (
			!isDeletedValue(overvoltageValue) &&
			(overvoltageValue < MIN_OVERVOLTAGE_LIMIT || overvoltageValue > MAX_OVERVOLTAGE_LIMIT)
		) {
			overvoltageError = $format('error_out_of_range', {
				default: `Value ${overvoltageValue} ${unit} is out of range. Please provide a value within the limits. (${MIN_OVERVOLTAGE_LIMIT} ${unit} — ${MAX_OVERVOLTAGE_LIMIT} ${unit})`,
				values: {
					value: overvoltageValue,
					min: MIN_OVERVOLTAGE_LIMIT,
					max: MAX_OVERVOLTAGE_LIMIT,
					unit,
				},
			});
		}
	};

	const setVoltageProtection = async () => {
		if (!hasChanges) {
			return;
		}

		overvoltageValue = isNaN(overvoltageValue) ? DEFAULT_OVERVOLTAGE_LIMIT : overvoltageValue;
		undervoltageValue = isNaN(undervoltageValue) ? DEFAULT_UNDERVOLTAGE_LIMIT : undervoltageValue;
		voltageThrValue = isNaN(voltageThrValue) ? DEFAULT_VOLTAGE_THR : voltageThrValue;
		autorecoveryDelayValue = isNaN(autorecoveryDelayValue)
			? DEFAULT_AUTORECOVERY_DELAY
			: autorecoveryDelayValue;

		const channelSettings = settings[componentChannelStructure] ?? {};
		const maxPowerLimit = DEVICE_MAX_POWER_DEFAULTS[device.type];
		const maxCurrentLimit = DEVICE_MAX_CURRENT_DEFAULTS[device.type];

		const config: {
			voltage_limit: number;
			undervoltage_limit?: number;
			autorecovery_enable?: boolean;
			voltage_thr?: number;
			autorecovery_delay?: number;
			power_limit?: number;
			current_limit?: number;
		} = {
			voltage_limit: overvoltageValue,
		};

		if (includeSiblingProtectionLimits) {
			const powerLimit = Number(channelSettings.power_limit);
			const currentLimit = Number(channelSettings.current_limit);
			if (!Number.isNaN(powerLimit) && maxPowerLimit !== undefined) {
				config.power_limit = Math.min(Math.max(powerLimit, 0), maxPowerLimit);
			} else if (!Number.isNaN(powerLimit)) {
				config.power_limit = powerLimit;
			}
			if (!Number.isNaN(currentLimit) && maxCurrentLimit !== undefined) {
				config.current_limit = Math.min(Math.max(currentLimit, 0), maxCurrentLimit);
			} else if (!Number.isNaN(currentLimit)) {
				config.current_limit = currentLimit;
			}
		}

		if (supportsUndervoltage) {
			config.undervoltage_limit = undervoltageValue;
		}

		if (supportsAutoRecovery) {
			config.autorecovery_enable = autoRecoveryEnabled;
			config.voltage_thr = voltageThrValue;
			config.autorecovery_delay = autorecoveryDelayValue;
		}

		loading = true;

		const res: SettingRequest = await gen2Request(device, `${deviceType}.setconfig`, {
			id: index,
			config,
		});

		if (res.isok) {
			settings[componentChannelStructure] = {
				...channelSettings,
				voltage_limit: overvoltageValue,
			};
			if (supportsUndervoltage) {
				settings[componentChannelStructure].undervoltage_limit = undervoltageValue;
			}
			if (supportsAutoRecovery) {
				settings[componentChannelStructure].autorecovery_enable = autoRecoveryEnabled;
				settings[componentChannelStructure].voltage_thr = voltageThrValue;
				settings[componentChannelStructure].autorecovery_delay = autorecoveryDelayValue;
			}
			if (includeSiblingProtectionLimits) {
				if (config.power_limit !== undefined) {
					settings[componentChannelStructure].power_limit = config.power_limit;
				}
				if (config.current_limit !== undefined) {
					settings[componentChannelStructure].current_limit = config.current_limit;
				}
			}
			devices_settings.update_devices_settings(id, settings);

			overvoltageCache = overvoltageValue;
			undervoltageCache = undervoltageValue;
			autoRecoveryCache = autoRecoveryEnabled;
			voltageThrCache = voltageThrValue;
			autorecoveryDelayCache = autorecoveryDelayValue;

			toast.open({
				type: 'success',
				title: $format('max_voltage_prot_save_scc_toast', {
					default: 'Successfully saved voltage protection value.',
				}),
			});
		} else {
			toast.open({
				type: 'danger',
				title: $format('max_voltage_prot_save_err_toast'),
			});
		}
		loading = false;
	};

	$: normalizedOvervoltage = isNaN(overvoltageValue) ? DEFAULT_OVERVOLTAGE_LIMIT : +overvoltageValue;
	$: normalizedUndervoltage = isNaN(undervoltageValue)
		? DEFAULT_UNDERVOLTAGE_LIMIT
		: +undervoltageValue;
	$: normalizedVoltageThr = isNaN(voltageThrValue) ? DEFAULT_VOLTAGE_THR : +voltageThrValue;
	$: normalizedAutorecoveryDelay = isNaN(autorecoveryDelayValue)
		? DEFAULT_AUTORECOVERY_DELAY
		: +autorecoveryDelayValue;
	$: hasChanges =
		isDeletedValue(overvoltageValue) ||
		normalizedOvervoltage !== +overvoltageCache ||
		(supportsUndervoltage &&
			(isDeletedValue(undervoltageValue) || normalizedUndervoltage !== +undervoltageCache)) ||
		(supportsAutoRecovery &&
			(autoRecoveryEnabled !== autoRecoveryCache ||
				isDeletedValue(voltageThrValue) ||
				normalizedVoltageThr !== +voltageThrCache ||
				isDeletedValue(autorecoveryDelayValue) ||
				normalizedAutorecoveryDelay !== +autorecoveryDelayCache));

	$: saveDisabled =
		loading ||
		!hasChanges ||
		!!overvoltageError ||
		(supportsUndervoltage && !!undervoltageError) ||
		(supportsAutoRecovery && (!!voltageThrError || !!autorecoveryDelayError));

	$: valueUndervoltage_valueOvervoltage = `${
		!isNaN(undervoltageValue) ? undervoltageValue : DEFAULT_UNDERVOLTAGE_LIMIT
	} - ${!isNaN(overvoltageValue) ? overvoltageValue : DEFAULT_OVERVOLTAGE_LIMIT}`;

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: setVoltageProtection,
				loading,
				disabled: saveDisabled,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Alert
	type="info"
	class="mb-s"
	heading={$format('delete_to_reset_default_protection_value', {
		default: `If you delete a value and save, the following default values will be applied:`,
	})}
	message={defaultsAlertMessage}
/>
<Text styleType="controls-small">
	{@html makeRangePrimaryAndBold(
		`${$format('voltage_prot_description', {
			values: { value: valueUndervoltage_valueOvervoltage },
			default: `Configure Shelly device to turn off when voltage exceeds the ${valueUndervoltage_valueOvervoltage} V range`,
		})}`,
	)}
</Text>

{#if supportsUndervoltage}
	<Text styleType="controls-small" class="mt-m mb-s">
		{$format('over_voltage_protection', {
			default: 'Over voltage protection',
		})}
	</Text>

	<Input
		on:input={(e) => {
			restrictDecimalNumberInput(e, 2);
			overvoltageValue = e.target['value'] === '' ? undefined : Number(e.target['value']);
			handleErrors();
		}}
		value={overvoltageValue}
		class="mb-s"
		size="m"
		type="number"
		min={MIN_OVERVOLTAGE_LIMIT}
		max={MAX_OVERVOLTAGE_LIMIT}
		info={$format('min_max_error_description', {
			values: {
				min: MIN_OVERVOLTAGE_LIMIT,
				max: MAX_OVERVOLTAGE_LIMIT,
			},
			default: `Value must be between ${MIN_OVERVOLTAGE_LIMIT} and ${MAX_OVERVOLTAGE_LIMIT}.`,
		})}
		error={overvoltageError}
		step={1}
		on:enter={setVoltageProtection}
		id={`input-${idPrefix}-overvoltage`}
		aria-label={$format('over_voltage_protection', {
			default: 'Over voltage protection',
		})}
	/>

	<Text styleType="controls-small" class="mt-s mb-s">
		{$format('under_voltage_protection', {
			default: 'Under voltage protection',
		})}
	</Text>

	<Input
		on:input={(e) => {
			restrictDecimalNumberInput(e, 2);
			undervoltageValue = e.target['value'] === '' ? undefined : Number(e.target['value']);
			handleErrors();
		}}
		value={undervoltageValue}
		size="m"
		class="mb-s"
		type="number"
		min={MIN_UNDERVOLTAGE_LIMIT}
		max={MAX_UNDERVOLTAGE_LIMIT}
		info={$format('min_max_error_description', {
			values: {
				min: MIN_UNDERVOLTAGE_LIMIT,
				max: MAX_UNDERVOLTAGE_LIMIT,
			},
			default: `Value must be between ${MIN_UNDERVOLTAGE_LIMIT} and ${MAX_UNDERVOLTAGE_LIMIT}.`,
		})}
		error={undervoltageError}
		step={1}
		on:enter={setVoltageProtection}
		id={`input-${idPrefix}-undervoltage`}
		aria-label={$format('under_voltage_protection', {
			default: 'Under voltage protection',
		})}
	/>
{:else}
	<Text styleType="controls-small" class="mt-m mb-s">
		{$format('over_voltage_protection', {
			default: 'Over voltage protection',
		})}
	</Text>

	<Input
		on:input={(e) => {
			restrictDecimalNumberInput(e, 2);
			overvoltageValue = e.target['value'] === '' ? undefined : Number(e.target['value']);
			handleErrors();
		}}
		value={overvoltageValue}
		class="mb-s"
		size="m"
		type="number"
		min={MIN_OVERVOLTAGE_LIMIT}
		max={MAX_OVERVOLTAGE_LIMIT}
		info={$format('min_max_error_description', {
			values: {
				min: MIN_OVERVOLTAGE_LIMIT,
				max: MAX_OVERVOLTAGE_LIMIT,
			},
			default: `Value must be between ${MIN_OVERVOLTAGE_LIMIT} and ${MAX_OVERVOLTAGE_LIMIT}.`,
		})}
		error={overvoltageError}
		step={1}
		on:enter={setVoltageProtection}
		id={`input-${idPrefix}-overvoltage`}
		aria-label={$format('over_voltage_protection', {
			default: 'Over voltage protection',
		})}
	/>
{/if}

{#if supportsAutoRecovery}
	<Checkbox
		size="m"
		class="mb-m"
		bind:checked={autoRecoveryEnabled}
		id={`checkbox-${idPrefix}-auto-recovery`}
		info={$format('delete_value_reset_default', {
			default: 'Deleting value will reset to default value.',
		})}
	>
		{$format('cb_autorecovery_enable_description', {
			default: 'Enable autorecovery from undervoltage or overvoltage events',
		})}
	</Checkbox>

	<Text styleType="controls-small" class="mb-s">
		{$format('cb_autorecovery_delta_in_volts', {
			default: 'Delta in volts',
		})}
	</Text>

	<Input
		on:input={(e) => {
			restrictDecimalNumberInput(e, 2);
			voltageThrValue = e.target['value'] === '' ? undefined : Number(e.target['value']);
			handleErrors();
		}}
		value={voltageThrValue}
		size="m"
		class="mb-s"
		type="number"
		min={MIN_VOLTAGE_THR}
		max={MAX_VOLTAGE_THR}
		info={$format('min_max_error_description', {
			values: {
				min: MIN_VOLTAGE_THR,
				max: MAX_VOLTAGE_THR,
			},
			default: `Value must be between ${MIN_VOLTAGE_THR} and ${MAX_VOLTAGE_THR}.`,
		})}
		error={voltageThrError}
		step={1}
		on:enter={setVoltageProtection}
		id={`input-${idPrefix}-voltage-thr`}
		aria-label={$format('cb_autorecovery_delta_in_volts', {
			default: 'Delta in volts',
		})}
	/>

	<Text styleType="controls-small" class="mb-s">
		{$format('cb_autorecovery_delay_in_seconds', {
			default: 'Delay in seconds',
		})}
	</Text>

	<Input
		on:input={(e) => {
			restrictDecimalNumberInput(e, 0);
			autorecoveryDelayValue = e.target['value'] === '' ? undefined : Number(e.target['value']);
			handleErrors();
		}}
		value={autorecoveryDelayValue}
		size="m"
		class="mb-s"
		type="number"
		min={MIN_AUTORECOVERY_DELAY}
		max={MAX_AUTORECOVERY_DELAY}
		info={$format('min_max_error_description', {
			values: {
				min: MIN_AUTORECOVERY_DELAY,
				max: MAX_AUTORECOVERY_DELAY,
			},
			default: `Value must be between ${MIN_AUTORECOVERY_DELAY} and ${MAX_AUTORECOVERY_DELAY}.`,
		})}
		error={autorecoveryDelayError}
		step={1}
		on:enter={setVoltageProtection}
		id={`input-${idPrefix}-autorecovery-delay`}
		aria-label={$format('cb_autorecovery_delay_in_seconds', {
			default: 'Delay in seconds',
		})}
	/>
{/if}

{#if !$showNewLayout}
	<Button
		class="mt-s"
		{loading}
		disabled={saveDisabled}
		size="m"
		width="full"
		on:click={setVoltageProtection}
		aria-label={$format('button_save', { default: 'Save' })}
	>
		{$format('button_save')}
	</Button>
{/if}
